import * as React from 'react';
import { useDebounce } from 'react-use';
import { Grid, TextField } from '@mui/material';

import { IAddress, IError } from 'models';

import { StatesSelect } from './states-select';

interface AddressInputProps {
  data?: IAddress;
  errors?: IError;
  visibleAddress2?: boolean;
  onChangeData?: Function;
}

const isSameProps = (prevProps: AddressInputProps, nextProps: AddressInputProps) => {
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
};

export const AddressInput = React.memo((props: AddressInputProps) => {
  const { data, errors, visibleAddress2 = false, onChangeData } = props;

  const [_data, setData] = React.useState<IAddress>({
    id: null,
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip: '',
  });

  const handleChangeData = (payload: { [key: string]: string }) => {
    setData({
      ..._data,
      ...payload,
    });
  };

  useDebounce(
    () => {
      if (onChangeData) {
        onChangeData(_data);
      }
    },
    100,
    [_data],
  );

  React.useEffect(() => {
    setData({
      id: null,
      address_1: data?.address_1 || '',
      address_2: data?.address_2 || '',
      city: data?.city || '',
      state: data?.state || '',
      zip: data?.zip || '',
    });
  }, [data]);

  return (
    <Grid container rowSpacing={3} columnSpacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={visibleAddress2 ? 'Address 1' : 'Address'}
          placeholder="e.g. 350 5th Ave"
          variant="outlined"
          value={_data.address_1}
          onChange={(event) =>
            handleChangeData({
              address_1: event.target.value,
            })
          }
          error={!!errors?.address_1}
          helperText={errors?.address_1}
        />
      </Grid>

      {visibleAddress2 && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address 2"
            variant="outlined"
            value={_data.address_2}
            onChange={(event) =>
              handleChangeData({
                address_2: event.target.value,
              })
            }
            error={!!errors?.address_2}
            helperText={errors?.address_2}
          />
        </Grid>
      )}

      <Grid item xs={5}>
        <TextField
          fullWidth
          label="City"
          placeholder="e.g. New York"
          variant="outlined"
          value={_data.city}
          onChange={(event) =>
            handleChangeData({
              city: event.target.value,
            })
          }
          error={!!errors?.city}
          helperText={errors?.city}
        />
      </Grid>

      <Grid item xs={4}>
        <StatesSelect
          label="State"
          variant="outlined"
          fullWidth
          size="medium"
          disabled={false}
          value={_data.state as string}
          onChange={(event: any) =>
            handleChangeData({
              state: event?.target?.value,
            })
          }
          error={!!errors?.state}
          helperText={errors?.state}
        />
      </Grid>

      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Zip"
          placeholder="e.g. 10118"
          variant="outlined"
          value={_data.zip}
          onChange={(event: any) =>
            handleChangeData({
              zip: event?.target?.value,
            })
          }
          error={!!errors?.zip}
          helperText={errors?.zip}
        />
      </Grid>
    </Grid>
  );
}, isSameProps);
